import { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Link,
  Typography,
} from "@mui/material";
import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import { FormSelectInput } from "@/components/form/FormSelectInput";
import { FormTextInput } from "@/components/form/FormTextInput";
import { FormButtonGroup } from "@/components/form/FormButtonGroup";
import { useMedicalReportsAgentContext } from "@/contexts/MedicalReportsAgentContext";
import { useGuidanceAgentContext } from "@/contexts/GuidanceAgentContext";
import { toast } from "react-toastify";
import { PostContract } from "@/services/fhir/contract/PostContract";
import { buildContractBody } from "./contractBody";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { useDiscussionCaseContext } from "@/contexts/DiscussionCaseContext";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { PrimaryButton } from "@/components/common";

const providerOptions = [{ value: "openai", display: "OpenAI" }];

interface ConfigureAIFormData {
  provider: "openai";
  apiKey: string;
}

interface ConfigureAIModalProps {
  open: boolean;
  onClose: () => void;
  initialData?: { provider: string; key: string } | null;
  isEnabled?: boolean;
  identifier: "MEDICAL_REPORTS_AGENT" | "GUIDANCE_AGENT" | "DISCUSSION_CASE_AGENT";
}

export const ConfigureAIModal: React.FC<ConfigureAIModalProps> = ({
  open,
  onClose,
  initialData,
  identifier,
}) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
    reset,
    setValue,
  } = useForm<ConfigureAIFormData>({
    defaultValues: {
      provider: "openai",
      apiKey: "",
    },
  });

  const { organizationId } = useOrganizationContext();
  const { user } = useSessionContext();
  const { refetchContract: refetchDiscussionCaseContract, contractDiscussionCase } = useDiscussionCaseContext();
  const { refetchContract: refetchMedicalReportsContract, contractMedicalReports } = useMedicalReportsAgentContext();
  const { refetchContract: refetchGuidanceContract, contractGuidance } = useGuidanceAgentContext();

  useEffect(() => {
    if (open) {
      if (initialData) {
        reset({
          provider: initialData.provider as "openai",
          apiKey: initialData.key,
        });
      } else {
        reset({
          provider: "openai",
          apiKey: "",
        });
      }
    }
  }, [open, initialData, reset]);

  const currentProvider = watch("provider");

  const getApiKeyLink = () => {
    switch (currentProvider) {
      case "openai":
        return "https://platform.openai.com/api-keys";
      default:
        return "";
    }
  };

  const validateApiKey = async (apiKey: string) => {
    const response = await fetch("https://api.openai.com/v1/models", {
      headers: { Authorization: `Bearer ${apiKey}` },
    });
    if (!response.ok) throw new Error("Chave OpenAI inválida");
  };

  const getRefetchFunction = () => {
    switch (identifier) {
      case "DISCUSSION_CASE_AGENT":
        return refetchDiscussionCaseContract;
      case "MEDICAL_REPORTS_AGENT":
        return refetchMedicalReportsContract;
      case "GUIDANCE_AGENT":
        return refetchGuidanceContract;

    }
  };

  function getApiKeyFromExtension(contract: any, url: string) {
    return (
      contract?.extension?.find((e: any) => e.url === url)?.valueString || ""
    );
  }

  const urls = {
    discussion: "https://fluxmed.com.br/fhir/extension/discussion_case_agent/api-key",
    medical: "https://fluxmed.com.br/fhir/extension/medical_reports_agent/api-key",
    guidance: "https://fluxmed.com.br/fhir/extension/guidance_agent/api-key",
  };

  const currentApiKey = watch("apiKey");

  // Função utilitária para filtrar chaves duplicadas
  function filterUniqueKeys(keys: any[]) {
    // Usando Set para garantir unicidade das chaves
    const seen = new Set();
    return keys.filter((item) => {
      if (!item || !item.value) return false;
      if (seen.has(item.value)) return false;
      seen.add(item.value);
      return true;
    });
  }

  const availableKeysRaw = [
    contractDiscussionCase && {
      label: "Agente de Discussão de Casos",
      value: getApiKeyFromExtension(contractDiscussionCase, urls.discussion),
    },
    contractMedicalReports && {
      label: "Agente de Relatórios Médicos",
      value: getApiKeyFromExtension(contractMedicalReports, urls.medical),
    },
    contractGuidance && {
      label: "Agente de Orientações ao Paciente",
      value: getApiKeyFromExtension(contractGuidance, urls.guidance),
    },
  ]
    .filter(Boolean)
    .filter((item: any) => item.value && item.value !== currentApiKey);

  // Filtra as chaves para mostrar apenas uma opção se forem iguais
  const availableKeys = filterUniqueKeys(availableKeysRaw);

  const onSubmit = async (data: ConfigureAIFormData) => {
    const refetch = getRefetchFunction();
    try {
      await validateApiKey(data.apiKey.trim());
      const contract = buildContractBody({
        provider: data.provider,
        apiKey: data.apiKey.trim(),
        cpf: user?.username, 
        nome: user?.name,
        email: user?.email?.[0], 
        organizationId: organizationId,
        identifier,
      });
      await PostContract({ data: contract });
      onClose();
      toast.success("Ativação realizada com sucesso!");
      if (refetch) refetch();
    } catch (err) {
      toast.error(
        "Erro ao validar a chave de API, verifique se a chave está correta"
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle textAlign="center">
          Adicionar credenciais
        </DialogTitle>
        <DialogContent>
        <Typography variant="subtitle2">
          Adicione sua chave de API para acessar os recursos de IA da plataforma, caso ainda não tenha, você pode obter no site do provedor <Link
                  href={getApiKeyLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  acessando o link
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </Link>.
        </Typography>
          <Box sx={{ mt: 2 }}>
            <FormSelectInput
              name="provider"
              control={control}
              label="Provedor"
              required={true}
              selectOptions={providerOptions}
            />

            <Box sx={{ mb: 2, mt: 2 }}>
              {availableKeys.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    Utilize uma chave já cadastrada:
                  </Typography>
                  {availableKeys.map((item, idx) =>
                    item ? (
                      <Box key={idx} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {item.label}
                        </Typography>
                        <PrimaryButton
                          onClick={() => setValue("apiKey", item.value, { shouldValidate: true })}
                          startIcon={<ContentCopyIcon />}
                          width="100%"
                          height="100%"
                        >
                          Usar esta chave
                        </PrimaryButton>
                      </Box>
                    ) : null
                  )}
                </Box>
              )}
            </Box>

            <FormTextInput
              name="apiKey"
              control={control}
              label="Chave de API"
              required={true}
              variant="filled"
            />
          </Box>
          <Box pt={3}>
            <FormButtonGroup
              onGoBackButtonClick={onClose}
              onNextButtonClick={handleSubmit(onSubmit)}
              loading={isSubmitting}
              disabled={isSubmitting}
              goBackButtonText="Cancelar"
              nextButtonText="Salvar"
              direction="row-reverse"
              maxWidth
            />
          </Box>
        </DialogContent>
      </form>
    </Dialog>
  );
};
